// apps/admin/src/pages/credits/CreditsFilters.tsx
//
// Phase 8.2 Wave 2 Task 07 — Filter bar for the cross-merchant Credits page.
//
// Per CONTEXT.md
//   • Billing tabs Credits surface = cross-merchant list.
//   • Saved Views per-user filter/sort/column snapshot.
//   • Credit schema versioned; filterable status enum:
//                active / used / expired / revoked.
//
// Surface contract:
//   - Merchant picker (free-text search OR explicit merchant id).
//   - Status chip group (active/used/expired/revoked + "All").
//   - Expiry range chips (within 7 / 30 / 90 days) — exclusive single select.
//   - Clear-filters CTA appears only when any filter is non-default.
//
// Stateless presentational component — parent (AdminCreditsPage) owns URL
// sync and Saved View hydration.

import { useEffect, useState, type JSX } from 'react';
import { Button, MerchantPicker } from '@printway/ui';
import type { FinanceCreditStatus } from '../../types/finance.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/** Stable key for SavedViewsDropdown — keep in sync with admin nav cookbook. */
export const CREDITS_TABLE_KEY = 'admin.credits.list';

/** Status options (enum + "All" pass-through). Labels resolved
 * at render-time via `t('credit.status.<value>')`. */
export const ADMIN_CREDIT_STATUS_OPTIONS: ReadonlyArray<
  FinanceCreditStatus | 'all'
> = ['active', 'used', 'expired', 'revoked', 'all'];

/** Expiry range presets — wire to BE `expires_within_days` int filter.
 * Labels resolved at render-time via t(). */
export const EXPIRY_RANGE_PRESETS: ReadonlyArray<number | ''> = [
  '',
  7,
  30,
  90,
];

export interface AdminCreditsFilterValue {
  /** Free-text search (merchant name / email; debounced 300ms). */
  q: string;
  /** Explicit merchant id (24-hex; usually populated via picker click). */
  merchantId: string;
  /** Single status select. */
  status: FinanceCreditStatus | 'all';
  /** Expiry-within window in days, or empty string for "any". */
  expiresWithinDays: number | '';
}

export interface CreditsFiltersProps {
  value: AdminCreditsFilterValue;
  onChange: (next: AdminCreditsFilterValue) => void;
}

export const defaultCreditsFilters: AdminCreditsFilterValue = {
  q: '',
  merchantId: '',
  status: 'active',
  expiresWithinDays: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreditsFilters({
  value,
  onChange,
}: CreditsFiltersProps): JSX.Element {
  const { t } = useTranslation('finance');
  // Cluster B audit (2026-05-23): the previous surface had TWO merchant
  // inputs — a debounced free-text `q` (name/email search) AND a raw
  // 24-hex ObjectId text input. Both collapsed into a single MerchantPicker
  // whose modal already debounces a name/email/ID search. The `q` URL
  // param is dropped from the source-of-truth; merchantId carries the
  // picked merchant.
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);

  // Clear label when filter is cleared externally (URL reset / back button).
  useEffect(() => {
    if (!value.merchantId) setMerchantLabel(null);
  }, [value.merchantId]);

  const hasAnyFilter =
    value.q.length > 0 ||
    value.merchantId.length > 0 ||
    value.status !== 'active' ||
    value.expiresWithinDays !== '';

  function clearAll(): void {
    setMerchantLabel(null);
    onChange({ ...defaultCreditsFilters });
  }

  return (
    <section
      className="flex flex-col gap-4 rounded-lg border border-pw-border bg-pw-bg p-4"
      data-testid="credits-filters"
    >
      {/* Row 1 — merchant picker + clear */}
      <div className="grid gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-xs font-medium text-pw-text-muted">
            {t('credit.filters.merchantLabel')}
          </span>
          <MerchantPicker
            value={value.merchantId || null}
            valueLabel={merchantLabel}
            onChange={(id, label) => {
              setMerchantLabel(label);
              // Drop the legacy `q` free-text field — the picker IS the
              // search now. Keep q='' in state so existing URLs that still
              // carry ?q=... clear gracefully.
              onChange({ ...value, q: '', merchantId: id ?? '' });
            }}
            placeholder={t('credit.filters.merchantPlaceholder')}
            testId="credits-filter-merchant-id"
          />
        </label>
        <div className="flex items-end justify-end">
          {hasAnyFilter ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              data-testid="credits-filter-clear"
            >
              {t('credit.filters.clear')}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Row 2 — status (single select) */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-pw-text-muted">
          {t('credit.filters.statusLabel')}
        </span>
        <div
          className="flex flex-wrap gap-1.5"
          role="radiogroup"
          aria-label={t('credit.filters.statusAria')}
        >
          {ADMIN_CREDIT_STATUS_OPTIONS.map((opt) => {
            const active = value.status === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange({ ...value, status: opt })}
                aria-pressed={active}
                role="radio"
                aria-checked={active}
                data-testid={
                  // Keep stable `credits-status-filter` test id on the
                  // ACTIVE chip so deep-link smoke tests can locate
                  // current selection — per task acceptance §test IDs.
                  active
                    ? 'credits-status-filter'
                    : `credits-status-filter-${opt}`
                }
                data-value={opt}
                className={
                  active
                    ? 'rounded-md border border-pw-primary bg-pw-primary/10 px-2 py-1 text-xs font-medium text-pw-primary-strong'
                    : 'rounded-md border border-pw-border bg-pw-bg px-2 py-1 text-xs text-pw-text-muted hover:bg-pw-surface'
                }
              >
                {t(`credit.status.${opt}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 3 — expiry range */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-pw-text-muted">
          {t('credit.filters.expiryLabel')}
        </span>
        <div
          className="flex flex-wrap gap-1.5"
          role="radiogroup"
          aria-label={t('credit.filters.expiryAria')}
        >
          {EXPIRY_RANGE_PRESETS.map((opt) => {
            const active = value.expiresWithinDays === opt;
            const key = opt === '' ? 'any' : String(opt);
            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  onChange({ ...value, expiresWithinDays: opt })
                }
                aria-pressed={active}
                role="radio"
                aria-checked={active}
                data-testid={`credits-filter-expiry-${key}`}
                className={
                  active
                    ? 'rounded-md border border-pw-primary bg-pw-primary/10 px-2 py-1 text-xs font-medium text-pw-primary-strong'
                    : 'rounded-md border border-pw-border bg-pw-bg px-2 py-1 text-xs text-pw-text-muted hover:bg-pw-surface'
                }
              >
                {opt === ''
                  ? t('credit.expiry.any')
                  : t('credit.expiry.withinDays', { days: opt })}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default CreditsFilters;
